import { ArrowUpRight, Plus, Search, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  Drawer,
  Empty,
  ErrorMessage,
  Loading,
  PageHeader,
  Pagination,
  StatusBadge,
} from '../components';
import { ContactDetails } from '../ContactDetails';
import { ContactEditForm } from '../ContactEditForm';
import { CompanyPicker } from '../DeviceForm';
import { SearchSelect } from '../SearchSelect';
import { usePermissions } from '../permissions';
import type { ContactOverviewResponse, ContactSyncStatus, Status } from '../types';

const pageSize = 50;
function SyncSummary({ items }: { items: ContactSyncStatus[] }) {
  const enabled = items.filter((item) => item.desired === 'ENABLED');
  const failed = enabled.filter((item) => item.actual === 'FAILED');
  const pending = enabled.filter((item) => item.actual !== 'READY' || item.needsRetry);
  if (!enabled.length) return <span className="text-xs text-muted">Nicht freigegeben</span>;
  return (
    <div>
      <StatusBadge status={failed.length ? 'FAILED' : pending.length ? 'PENDING' : 'READY'} />
      <div className="mt-1 text-xs text-muted">
        {failed.length
          ? `${failed.length} mit Fehler`
          : pending.length
            ? `${pending.length} Übernahmen offen`
            : `${enabled.length} Anwendungen`}
      </div>
    </div>
  );
}
export function ContactsPage() {
  const can = usePermissions();
  const [data, setData] = useState<ContactOverviewResponse>({
    items: [],
    instances: [],
    page: 1,
    pageSize,
    total: 0,
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [organization, setOrganization] = useState<'assigned' | 'unassigned' | ''>('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<unknown>();
  const [formError, setFormError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      if (organization) params.set('organization', organization);
      void api<ContactOverviewResponse>(`/api/contacts?${params}`, { signal: controller.signal })
        .then((result) => {
          setData(result);
          setError(undefined);
        })
        .catch((caught) => {
          if (!controller.signal.aborted) setError(caught);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [organization, page, search, status, refresh]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setFormError(undefined);
    try {
      await api(companyId ? `/api/companies/${companyId}/contacts` : '/api/contacts', {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          firstName: form.get('firstName'),
          lastName: form.get('lastName'),
          jobTitle: form.get('jobTitle') || null,
          identities: form.get('email')
            ? [{ type: 'EMAIL', value: form.get('email'), isPrimary: true }]
            : [],
        }),
      });
      setShowForm(false);
      setPage(1);
      setRefresh((value) => value + 1);
    } catch (caught) {
      setFormError(caught);
    } finally {
      setSaving(false);
    }
  }
  const selected = data.items.find((contact) => contact.id === selectedId);
  return (
    <>
      <PageHeader
        title="Kontakte"
        description="Ansprechpartner finden, zuordnen und ihre Anwendungsfreigaben prüfen."
        actions={
          can('core:contacts:write') && (
            <button
              className="button"
              onClick={() => {
                setFormError(undefined);
                setCompanyId('');
                setShowForm(true);
              }}
            >
              <Plus size={16} />
              Kontakt anlegen
            </button>
          )
        }
      />
      <div className="filter-bar">
        <label>
          <span className="label">Kontakte suchen</span>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-muted" size={16} />
            <input
              className="input w-full pl-9"
              placeholder="Name, E-Mail, Telefon oder Kunde…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </label>
        <label>
          <span className="label">Status</span>
          <SearchSelect
            className="input w-full"
            value={status}
            onValueChange={(value) => {
              setStatus(value as Status | '');
              setPage(1);
            }}
          >
            <option value="">Alle Status</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="INACTIVE">Inaktiv</option>
            <option value="ARCHIVED">Archiviert</option>
          </SearchSelect>
        </label>
        <label>
          <span className="label">Zuordnung</span>
          <SearchSelect
            className="input w-full"
            value={organization}
            onValueChange={(value) => {
              setOrganization(value as typeof organization);
              setPage(1);
            }}
          >
            <option value="">Alle Kontakte</option>
            <option value="assigned">Mit Kunde</option>
            <option value="unassigned">Ohne Kunde</option>
          </SearchSelect>
        </label>
        {(search || status || organization) && (
          <button
            className="button-secondary"
            onClick={() => {
              setSearch('');
              setStatus('');
              setOrganization('');
              setPage(1);
            }}
          >
            <X size={14} />
            Zurücksetzen
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
      <div className="panel overflow-x-auto" aria-busy={loading}>
        {loading ? (
          <Loading>Kontakte werden geladen…</Loading>
        ) : error ? (
          <Empty>
            Kontakte konnten nicht geladen werden.
            <button className="button-secondary" onClick={() => setRefresh((value) => value + 1)}>
              Erneut versuchen
            </button>
          </Empty>
        ) : !data.items.length ? (
          <Empty>
            {search || status || organization
              ? 'Keine Kontakte für diese Filter.'
              : 'Noch keine Kontakte angelegt.'}
          </Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Kontakt</th>
                <th>Kunde</th>
                <th>Kontaktweg</th>
                <th>Status</th>
                <th>Anwendungen</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((contact) => {
                const identities = contact.identities.filter((identity) => !identity.revokedAt);
                const email =
                  identities.find((identity) => identity.type === 'EMAIL' && identity.isPrimary) ??
                  identities.find((identity) => identity.type === 'EMAIL');
                const phone =
                  identities.find(
                    (identity) => ['PHONE', 'MOBILE'].includes(identity.type) && identity.isPrimary,
                  ) ?? identities.find((identity) => ['PHONE', 'MOBILE'].includes(identity.type));
                return (
                  <tr key={contact.id}>
                    <td>
                      <button
                        className="inline-flex items-center gap-2 text-left font-semibold hover:text-accent"
                        onClick={() => {
                          setSelectedId(contact.id);
                          setEditing(false);
                          setFormError(undefined);
                        }}
                      >
                        {contact.displayName}
                        <ArrowUpRight size={13} className="text-muted" />
                      </button>
                      {contact.jobTitle && (
                        <div className="text-xs text-muted">{contact.jobTitle}</div>
                      )}
                      {contact.mergeTargetId && (
                        <div className="mt-1 text-xs text-amber-800">Zusammengeführt</div>
                      )}
                    </td>
                    <td>
                      {contact.organization ? (
                        <Link
                          className="text-accent hover:underline"
                          to={`/companies/${contact.organization.id}`}
                        >
                          {contact.organization.name}
                        </Link>
                      ) : (
                        <span className="text-muted">Nicht zugeordnet</span>
                      )}
                    </td>
                    <td>
                      <div className="max-w-64 break-words">
                        {email?.value ?? phone?.value ?? '—'}
                        {email && phone && (
                          <div className="mt-1 text-xs text-muted">{phone.value}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={contact.status} />
                    </td>
                    <td>
                      <SyncSummary items={contact.syncStatuses} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data.total}
        loading={loading || Boolean(error)}
        onPageChange={setPage}
      />
      {selected && (
        <Drawer
          title={selected.displayName}
          description="Kontaktdetails und Anwendungsfreigaben"
          onClose={() => setSelectedId(undefined)}
        >
          <ErrorMessage error={formError} />
          {editing ? (
            <ContactEditForm
              contact={selected}
              onCancel={() => setEditing(false)}
              onError={setFormError}
              onSaved={async () => {
                setRefresh((value) => value + 1);
                setEditing(false);
              }}
            />
          ) : (
            <>
              {can('core:contacts:write') &&
                selected.status !== 'ARCHIVED' &&
                !selected.mergeTargetId && (
                  <button className="button-secondary" onClick={() => setEditing(true)}>
                    Kontakt bearbeiten
                  </button>
                )}
              <ContactDetails
                key={selected.id}
                contact={selected}
                onChanged={() => setRefresh((value) => value + 1)}
              />
            </>
          )}
        </Drawer>
      )}
      {showForm && (
        <Drawer
          title="Kontakt anlegen"
          description="Kontaktwege und Kundenzuordnung sind optional."
          onClose={() => {
            if (!saving) setShowForm(false);
          }}
        >
          <ErrorMessage error={formError} />
          <form onSubmit={create} className="grid gap-5 sm:grid-cols-2">
            <label>
              <span className="label">Vorname *</span>
              <input name="firstName" required maxLength={100} className="input w-full" autoFocus />
            </label>
            <label>
              <span className="label">Nachname *</span>
              <input name="lastName" required maxLength={100} className="input w-full" />
            </label>
            <label>
              <span className="label">Position</span>
              <input name="jobTitle" className="input w-full" />
            </label>
            <label>
              <span className="label">E-Mail</span>
              <input name="email" type="email" className="input w-full" />
            </label>
            {can('core:companies:read') && (
              <CompanyPicker value={companyId} onChange={setCompanyId} />
            )}
            <div className="flex gap-2">
              <button className="button" disabled={saving}>
                {saving ? 'Wird gespeichert…' : 'Kontakt anlegen'}
              </button>
              <button
                type="button"
                className="button-secondary"
                disabled={saving}
                onClick={() => setShowForm(false)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Drawer>
      )}
    </>
  );
}
